// ============================================================================
// Safe Care - Demande de suspension (côté agent)
// Module : features/assistance_sociale/pages
// Route  : /suspension/demande
// Flux   : tableau des souscriptions ACTIVE -> "Demander la suspension"
//          -> modale (motif + justificatifs configurés dans la version,
//          contexte RUPTURE) -> DEMANDESUSPENSION EN_ATTENTE + documents GED.
//          La souscription reste ACTIVE jusqu'à validation (/suspension/valide).
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input, Upload } from 'antd';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { UploadOutlined, FilePdfOutlined, DeleteOutlined, EyeOutlined, UserOutlined, FileTextOutlined, ContainerFilled } from '@ant-design/icons';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchModal from '../../../Components/SearchModal';
import ParamsPicklist from '../../../Types/ParamsPicklist';
import {
  useGetAllSouscriptions,
  useDemanderSuspension,
  useAddDemandeSuspensionDocument,
  useGetDocumentsRequisBySouscription,
} from '../hooks';
import { SouscriptionSafeCare, DocumentRequis } from '../types';

const { TextArea } = Input;

// Picklist des motifs de suspension (table LIB, rubrique MOTSUSP — script 25_motifs_suspension.sql)
const searchMotifSusp: ParamsPicklist = { CodeAgence: 'MOTSUSP', Table: 'LIB', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: 'RUBRIQ', Choix: 22 };

const SuspensionDemandePage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: souscriptions, isLoading } = useGetAllSouscriptions('ACTIVE');
  const demanderSuspension = useDemanderSuspension();
  const addDemandeSuspensionDocument = useAddDemandeSuspensionDocument();

  const [loading, setLoading] = useState(false);
  const [Data, SetData] = useState<SouscriptionSafeCare[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);

  // --- Modale suspension avec documents (identique à l'ancienne page interruption) ---
  const [modalSuspension, setModalSuspension] = useState(false);
  const [motifSuspension, setMotifSuspension] = useState('');
  const [formMotif] = Form.useForm();
  const [pendingDocs, setPendingDocs] = useState<{ name: string; dataUri: string; isPdf: boolean; idDocumentRequis?: number; libelleDocument?: string }[]>([]);
  const [previewDoc, setPreviewDoc] = useState<{ name: string; dataUri: string; isPdf: boolean } | null>(null);
  const [docRequisSel, setDocRequisSel] = useState<DocumentRequis | null>(null);
  const [picklistDocOpen, setPicklistDocOpen] = useState(false);
  const [picklistDocSearch, setPicklistDocSearch] = useState('');
  const [picklistDocSelKeys, setPicklistDocSelKeys] = useState<React.Key[]>([]);

  // Documents requis pour la suspension/rupture, configurés dans l'onglet "Docs suspension" de la version
  const { data: documentsRequisRes } = useGetDocumentsRequisBySouscription(selectedSouscription?.IdSouscription, 'RUPTURE');
  const documentsRequis: DocumentRequis[] =
    documentsRequisRes?.StatutCode === 200 ? documentsRequisRes.Result?.Table1 ?? [] : [];

  const fileToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const handleUploadSuspensionDoc = async (file: File) => {
    if (file.size / 1024 > 2000) {
      SweetAlert.fire({ title: 'Fichier trop volumineux', text: `"${file.name}" dépasse 2 Mo.`, icon: 'warning' });
      return false;
    }
    const dataUri = await fileToDataUri(file);
    const isPdf = file.type === 'application/pdf';
    setPendingDocs((prev) => [...prev, {
      name: file.name,
      dataUri,
      isPdf,
      idDocumentRequis: docRequisSel?.IdDocumentRequis,
      libelleDocument: docRequisSel?.LibelleDocument,
    }]);
    setDocRequisSel(null);
    return false;
  };

  const removePendingDoc = (idx: number) => {
    setPendingDocs((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    if (souscriptions) SetData(souscriptions);
  }, [souscriptions]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return Data;
    const term = searchTerm.toLowerCase();
    return Data.filter((d) =>
      [d.Numcli, d.NOMCLI, d.PRECLI, d.NomProduit, d.NumeroVersion, d.NumCptDebit]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [Data, searchTerm]);

  const columns: ColumnsType<SouscriptionSafeCare> = [
    { title: 'N°', dataIndex: 'IdSouscription', width: 70 },
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Nom', dataIndex: 'NOMCLI', ellipsis: true },
    { title: 'Prénom', dataIndex: 'PRECLI', ellipsis: true },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 75 },
    { title: 'Compte', dataIndex: 'NumCptDebit', width: 130 },
    {
      title: 'Cotisation',
      dataIndex: 'MontantCotisation',
      width: 110,
      align: 'right',
      render: (v: number) => (v ?? 0).toLocaleString('fr-FR'),
    },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 110, render: (v: string) => (v ? String(v).substring(0, 10) : '') },
    {
      title: 'Statut',
      dataIndex: 'Statut',
      width: 90,
      render: (v: string) => <span style={{ color: 'var(--darkColor)', fontWeight: 600 }}>{v}</span>,
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: SouscriptionSafeCare[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d) => d.IdSouscription === newSelectedRowKeys?.[0]);
    setSelectedSouscription(record ?? null);
  };

  const resetSelection = () => {
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
  };

  const openModalSuspension = () => {
    if (!selectedSouscription) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription active', icon: 'warning' });
      return;
    }
    setMotifSuspension('');
    formMotif.resetFields();
    setPendingDocs([]);
    setDocRequisSel(null);
    setPicklistDocSearch('');
    setPicklistDocSelKeys([]);
    setModalSuspension(true);
  };

  const confirmSuspension = async () => {
    if (!selectedSouscription) return;
    if (!motifSuspension.trim()) {
      SweetAlert.fire({ title: 'Motif obligatoire', icon: 'warning' });
      return;
    }
    // Des labels sont configurés pour la version : exiger au moins un document labellisé
    if (documentsRequis.length > 0 && !pendingDocs.some((p) => p.idDocumentRequis)) {
      SweetAlert.fire({
        title: 'Documents requis non fournis',
        text: 'Sélectionnez les types de documents via la liste et uploadez les justificatifs correspondants.',
        icon: 'warning',
      });
      return;
    }
    // Documents obligatoires configurés (contexte RUPTURE) : chacun doit être fourni
    const manquants = documentsRequis
      .filter((d) => d.Obligatoire)
      .filter((d) => !pendingDocs.some((p) => p.idDocumentRequis === d.IdDocumentRequis));
    if (manquants.length > 0) {
      SweetAlert.fire({
        title: 'Documents obligatoires manquants',
        text: `Veuillez fournir : ${manquants.map((m) => m.LibelleDocument).join(', ')}`,
        icon: 'warning',
      });
      return;
    }
    if (pendingDocs.length === 0) {
      SweetAlert.fire({ title: 'Au moins un document justificatif est obligatoire', icon: 'warning' });
      return;
    }
    setLoading(true);
    // 1. Créer la demande de suspension (EN_ATTENTE) — la souscription reste ACTIVE
    let idDemandeSuspension: number | null = null;
    try {
      const res = await demanderSuspension.mutateAsync({
        idSouscription: selectedSouscription.IdSouscription!,
        motif: motifSuspension,
        utiSai: user.coduti ?? '',
      });
      if (res?.StatutCode !== 200) {
        setLoading(false);
        SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        return;
      }
      // La SP retourne l'ID dans la ligne de statut -> Result.Table[0] (comme IdDemandeAssistance)
      idDemandeSuspension = res?.Result?.Table?.[0]?.IdDemandeSuspension ?? res?.Result?.Table1?.[0]?.IdDemandeSuspension ?? null;
    } catch {
      setLoading(false);
      SweetAlert.fire({ title: 'Erreur', text: 'Échec de la création de la demande de suspension', icon: 'error' });
      return;
    }
    if (!idDemandeSuspension) {
      setLoading(false);
      SweetAlert.fire({ title: 'Erreur', text: 'Demande créée mais identifiant introuvable.', icon: 'error' });
      return;
    }
    // 2. Enregistrer les documents rattachés à la demande dans DOCUMENT (GED)
    let docsOk = 0;
    let docsErr = 0;
    for (const doc of pendingDocs) {
      try {
        const res = await addDemandeSuspensionDocument.mutateAsync({
          idDemandeSuspension,
          description: doc.libelleDocument ? `${doc.libelleDocument} — ${doc.name}` : doc.name,
          pieceJointe: doc.dataUri,
          nomFichier: doc.name,
          utiSai: user.coduti ?? '',
          idDocumentRequis: doc.idDocumentRequis,
        });
        if (res?.StatutCode === 200) docsOk++;
        else docsErr++;
      } catch {
        docsErr++;
      }
    }
    setLoading(false);
    setModalSuspension(false);
    SweetAlert.fire({
      title: 'Demande envoyée',
      text: `Demande de suspension en attente de validation. ${docsOk} document(s) enregistré(s)${docsErr > 0 ? `, ${docsErr} en échec` : ''}.`,
      icon: 'success',
    });
    resetSelection();
  };

  const items = [
    {
      key: '1',
      label: <span id="Tab">Demande de suspension</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography variant="h6" className="formTitle">
        Demande de suspension
      </Typography>
      <div className="clientP">
        <div>
          <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
        </div>
      </div>
      <Spin spinning={isLoading || loading} tip="En Cours de Traitement ..." style={{ color: '#116599' }}>
        <div className="tabItem">
          <Box display="flex" fontStyle="inherit" className="border-left" sx={{ p: 2, my: 5 }}>
            <div style={{ width: '100%' }}>
              <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }} wrap>
                <Input.Search
                  placeholder="Rechercher une souscription..."
                  allowClear
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
                <Button
                  type="primary"
                  disabled={!selectedSouscription}
                  onClick={openModalSuspension}
                >
                  Demander la suspension
                </Button>
              </Space>
              <Table
                size="small"
                style={{ width: '100%' }}
                rowKey="IdSouscription"
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys,
                  onChange: onSelectChange,
                }}
                onRow={(record) => ({
                  onClick: () => {
                    const key = record.IdSouscription!;
                    const already = selectedRowKeys.length === 1 && selectedRowKeys[0] === key;
                    if (already) {
                      setSelectedRowKeys([]);
                      setSelectedSouscription(null);
                    } else {
                      onSelectChange([key], [record]);
                    }
                  },
                })}
                columns={columns}
                dataSource={filteredData}
                scroll={{ y: 320, x: 10 }}
                pagination={{ defaultPageSize: 8, pageSizeOptions: [8, 15, 30, 50], showSizeChanger: true, position: ['bottomRight'] }}
              />
            </div>
          </Box>
        </div>
      </Spin>

      {/* Modale de suspension avec documents + motif — même logique que l'ancienne page interruption.
          zIndex 1200 : au-dessus du chrome applicatif mais sous les Dialog MUI (1300)
          pour que la picklist des documents reste visible par-dessus. */}
      <Modal
        title="Suspension de la souscription"
        open={modalSuspension}
        onOk={confirmSuspension}
        onCancel={() => setModalSuspension(false)}
        okText="Envoyer la demande"
        cancelText="Annuler"
        okButtonProps={{ loading: loading }}
        width={620}
        centered
        zIndex={1200}
        className="safeCareModal"
      >
        {selectedSouscription && (
          <Box sx={{ mb: 2 }}>
            <p>Client : <b>{selectedSouscription.Numcli}</b></p>
            <p>Produit : <b>{selectedSouscription.NomProduit}</b> (v{selectedSouscription.NumeroVersion})</p>
          </Box>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
            Motif de la suspension <span style={{ color: 'red' }}>*</span>
          </label>
          {/* Picklist sur la table LIB (rubrique MOTSUSP) — le libellé choisi est enregistré comme motif */}
          <Form component={false} form={formMotif}>
            <SearchModal
              width={12}
              Titre="Motif de suspension"
              label=""
              name="MotifSusp"
              Taille1={167}
              Taille2={320}
              Parametre={searchMotifSusp}
              form={formMotif}
              ExecuteafterSelect1={() => setMotifSuspension(String(formMotif.getFieldValue('lib_MotifSusp') ?? ''))}
            />
          </Form>
          {!motifSuspension && (
            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
              Sélectionnez un motif dans la liste.
            </div>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
            Justificatifs (documents) <span style={{ color: 'red' }}>*</span>
          </label>

          {documentsRequis.length > 0 ? (
            <div>
              {/* Tags de statut des documents requis configurés (contexte RUPTURE) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {documentsRequis.map((d) => {
                  const fourni = pendingDocs.some((p) => p.idDocumentRequis === d.IdDocumentRequis);
                  return (
                    <span
                      key={d.IdDocumentRequis}
                      title={d.Description}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 10,
                        border: `1px solid ${fourni ? 'var(--darkColor)' : '#d4380d'}`,
                        background: fourni ? 'var(--lightColor002)' : '#fff1f0',
                        color: fourni ? 'var(--darkColor)' : '#d4380d',
                      }}
                    >
                      {d.Obligatoire ? '* ' : ''}
                      {d.LibelleDocument}
                      {fourni ? ' ✓' : ' ✗'}
                    </span>
                  );
                })}
              </div>

              {/* Picklist (style SearchModal) pour sélectionner le type de document */}
              {!docRequisSel && (
                <div style={{ marginBottom: 8 }}>
                  <Space.Compact block>
                    <Input.Search
                      placeholder="Rechercher..."
                      value={picklistDocSearch}
                      onChange={(e) => setPicklistDocSearch(e.target.value)}
                      onSearch={() => setPicklistDocOpen(true)}
                      style={{ width: 167 }}
                    />
                    <Input
                      disabled
                      placeholder="Sélectionnez le type de document"
                      value=""
                      style={{ width: 320 }}
                    />
                  </Space.Compact>
                </div>
              )}

              {/* Champ upload lié au label choisi */}
              {docRequisSel && (
                <div style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid var(--colorOpaque030)',
                  background: 'var(--lightColor001)',
                  marginBottom: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <b style={{ color: 'var(--darkColor)' }}>{docRequisSel.LibelleDocument}</b>
                      {docRequisSel.Obligatoire && <span style={{ color: '#d4380d' }}> *</span>}
                      {docRequisSel.Description && (
                        <div style={{ fontSize: 11, color: '#666' }}>{docRequisSel.Description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDocRequisSel(null)}
                      style={{
                        border: 'none', background: 'transparent',
                        color: '#ff4d4f', cursor: 'pointer', fontSize: 16,
                      }}
                      title="Annuler la sélection"
                    >
                      ✕
                    </button>
                  </div>
                  <Upload.Dragger
                    accept=".pdf,.jpg,.jpeg,.png"
                    showUploadList={false}
                    beforeUpload={(file) => { handleUploadSuspensionDoc(file); return false; }}
                  >
                    <div style={{ padding: '4px 0' }}>
                      <UploadOutlined style={{ fontSize: 22, color: '#116599' }} />
                      <div style={{ fontSize: 12, marginTop: 4, color: '#8c8c8c' }}>
                        Uploadez le fichier pour « {docRequisSel.LibelleDocument} » (PDF, JPG, PNG — max 2 Mo)
                      </div>
                    </div>
                  </Upload.Dragger>
                </div>
              )}
            </div>
          ) : (
            /* Aucun document configuré pour la version : upload libre */
            <Upload.Dragger
              multiple
              showUploadList={false}
              accept=".pdf,.jpg,.jpeg,.png"
              beforeUpload={(file) => { handleUploadSuspensionDoc(file); return false; }}
            >
              <p style={{ fontSize: 13 }}>
                <UploadOutlined style={{ fontSize: 22, color: '#116599' }} />
                <br />
                Cliquez ou glissez les documents ici (PDF, JPG, PNG — max 2 Mo chacun)
              </p>
            </Upload.Dragger>
          )}

          {pendingDocs.length === 0 && (
            <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
              Au moins un document justificatif est obligatoire
            </div>
          )}
        </div>

        {/* Vignettes de prévisualisation */}
        {pendingDocs.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {pendingDocs.map((doc, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  width: 92,
                  height: 92,
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                }}
              >
                {doc.isPdf ? (
                  <div style={{ textAlign: 'center' }}>
                    <FilePdfOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />
                    <div style={{ fontSize: 9, padding: '0 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.libelleDocument ?? doc.name}
                    </div>
                  </div>
                ) : (
                  <img
                    src={doc.dataUri}
                    alt={doc.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
                {doc.libelleDocument && !doc.isPdf && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: 9, padding: '0 4px', background: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.libelleDocument}
                  </div>
                )}
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  style={{ position: 'absolute', top: 2, left: 2 }}
                  onClick={() => setPreviewDoc(doc)}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ position: 'absolute', top: 2, right: 2 }}
                  onClick={() => removePendingDoc(idx)}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Modale de prévisualisation d'un document — au-dessus de la modale suspension (1200), sous les Dialog MUI (1300) */}
      <Modal
        title={previewDoc?.name}
        open={!!previewDoc}
        onCancel={() => setPreviewDoc(null)}
        footer={null}
        width={700}
        centered
        zIndex={1250}
        className="safeCareModal"
      >
        {previewDoc && (
          previewDoc.isPdf ? (
            <iframe
              src={previewDoc.dataUri}
              title={previewDoc.name}
              style={{ width: '100%', height: '500px', border: 'none' }}
            />
          ) : (
            <img
              src={previewDoc.dataUri}
              alt={previewDoc.name}
              style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
            />
          )
        )}
      </Modal>

      {/* Picklist des types de documents requis (RUPTURE) — même UX que l'indemnisation */}
      <Dialog
        className="modal"
        onClick={() => setPicklistDocOpen(false)}
        sx={{ '& .MuiDialog-paper': { minWidth: 'sm', maxHeight: 'md' } }}
        maxWidth="md"
        open={picklistDocOpen}
        onClose={() => { setPicklistDocOpen(false); setPicklistDocSearch(''); setPicklistDocSelKeys([]); }}
      >
        <DialogTitle>Sélectionnez le type de document</DialogTitle>
        <DialogContent dividers></DialogContent>
        <Spin spinning={false} tip="En Cour de Traitement ..." style={{ color: '#116599' }}>
          <Stack direction="row" spacing={5}>
            <Form name="picklistDocForm" wrapperCol={{ span: 16 }} style={{ minWidth: 600, marginLeft: 50 }} initialValues={{ remember: true }}>
              <Tabs defaultActiveKey="3" items={[
                {
                  key: '1',
                  label: <span id="Tab"><Typography><UserOutlined />IDENTIFIANT</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="IDENTIFIANT" />,
                },
                {
                  key: '2',
                  label: <span id="Tab"><Typography><FileTextOutlined />LIBELLE</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="LIBELLE" />,
                },
                {
                  key: '3',
                  label: <span id="Tab"><Typography><ContainerFilled />ALL</Typography></span>,
                  children: (
                    <Box fontStyle="inherit" display="flex">
                      <InputForm
                        noupercase
                        taille={600}
                        autoFocus
                        notRequired
                        name="text"
                        type="text"
                        label="TEXT"
                        onChange={(e: any) => setPicklistDocSearch(e.target.value.trim())}
                      />
                      <hr />
                      <Button style={{ top: '10px' }}>Rechercher</Button>
                    </Box>
                  ),
                },
              ]} />
            </Form>
          </Stack>
          <DialogContent dividers>
            <Table
              style={{ width: 'auto' }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: picklistDocSelKeys,
                onChange: (keys: React.Key[], rows: any[]) => {
                  setPicklistDocSelKeys(keys);
                  if (rows[0]) {
                    const d = rows[0] as DocumentRequis;
                    setDocRequisSel(d);
                    setPicklistDocOpen(false);
                    setPicklistDocSearch('');
                    setPicklistDocSelKeys([]);
                  }
                },
              }}
              scroll={{ y: 250, x: 10 }}
              rowKey="IdDocumentRequis"
              columns={[
                { title: 'Code', dataIndex: 'Ordre', width: 60 },
                {
                  title: 'Libelle',
                  dataIndex: 'LibelleDocument',
                  filteredValue: picklistDocSearch ? [picklistDocSearch] : [],
                  onFilter: (value: any, record: any) =>
                    String(record.LibelleDocument).toLowerCase().includes(String(value).toLowerCase()),
                  render: (text: string, record: DocumentRequis) => (
                    <span>
                      {text}
                      {record.Obligatoire && <span style={{ color: '#d4380d', marginLeft: 6 }}>*</span>}
                    </span>
                  ),
                },
                { title: 'Description', dataIndex: 'Description', ellipsis: true },
              ]}
              dataSource={documentsRequis.filter((d) => {
                const fourni = pendingDocs.some((p) => p.idDocumentRequis === d.IdDocumentRequis);
                return !fourni;
              })}
            />
          </DialogContent>
        </Spin>
        <DialogActions>
          <Button type="default" danger onClick={() => { setPicklistDocOpen(false); setPicklistDocSearch(''); setPicklistDocSelKeys([]); }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuspensionDemandePage;
